
import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { useArtistData } from '@/hooks/useArtistData';
import { Button } from '@/components/ui/button';
import { Music } from 'lucide-react';
import { toast } from "sonner";
import PromoteTrackButton from './dashboard/PromoteTrackButton';
import DashboardStats from './dashboard/DashboardStats';
import UploadForm from './dashboard/UploadForm';
import TrackCreatedModal from './tracks/TrackCreatedModal';
import { useDashboardState, timeRanges } from '@/hooks/useDashboardState';
import { useMockData } from '@/hooks/useMockData';
import { useSuccessModal } from '@/hooks/useSuccessModal';
import { generateMockData } from './dashboard/stats/mockDataUtils';

const Dashboard: React.FC = () => {
  const { uploads, isLoading, artistId, addUpload, removeUpload, refreshData } = useArtistData();
  const topRef = useRef<HTMLDivElement>(null);
  const { 
    timeRange, 
    setTimeRange, 
    activeTab, 
    handlePromoteTrack,
    isRefreshing,
    setIsRefreshing
  } = useDashboardState();
  
  const { mockUploads, useMockData: useMockDataValue, setMockUploads } = useMockData(uploads, isLoading);
  const { showSuccessModal, newTrack, isSendingNotification, handleSuccess, handleCloseModal } = useSuccessModal(topRef);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    
    try {
      if (useMockDataValue) {
        const mockData = generateMockData();
        setMockUploads(mockData);
        toast.success("Data refreshed successfully", {
          position: "bottom-center"
        });
      } else {
        await refreshData();
        toast.success("Data refreshed successfully", {
          position: "bottom-center"
        });
      }
    } catch (error) {
      console.error("Error refreshing data:", error);
      toast.error("Failed to refresh data", {
        position: "bottom-center"
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  // Filter uploads based on selected time range
  const activeUploads = useMockDataValue ? mockUploads : uploads;
  
  if (isLoading) {
    return (
      <div className="w-full py-12 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const filteredUploads = activeUploads ? activeUploads.filter(upload => {
    if (timeRange === 'all') return true;
    
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - timeRanges[timeRange as keyof typeof timeRanges].days);
    
    return new Date(upload.createdAt) >= cutoffDate;
  }) : [];

  return (
    <div ref={topRef} className="animate-fade-in w-full max-w-4xl mx-auto px-4">
      <div className="flex justify-between mb-6">
        <Link to="/tracks">
          <Button variant="outline" size="sm" className="gap-2">
            <Music size={16} />
            View Tracks
          </Button>
        </Link>
      </div>
      
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Artist Dashboard</h1>
        <p className="text-muted-foreground">
          Create and manage your Spotify downloads
        </p>
      </div>
      
      <PromoteTrackButton activeTab={activeTab} onClick={handlePromoteTrack} />
      
      {activeTab === 'stats' && (
        <DashboardStats
          filteredUploads={filteredUploads}
          timeRange={timeRange}
          setTimeRange={setTimeRange}
          timeRanges={timeRanges}
          useMockData={useMockDataValue}
          handleRefresh={handleRefresh}
          isRefreshing={isRefreshing}
        />
      )}
      
      {activeTab === 'create' && (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold">Create Track Download</h2>
          <p className="text-muted-foreground mb-4">
            Fill out the form below to create a new track download.
          </p>
          <UploadForm 
            onUploadSuccess={addUpload} 
            artistId={artistId} 
            onSuccess={handleSuccess}
          />
        </div>
      )}

      {newTrack && (
        <TrackCreatedModal
          isOpen={showSuccessModal}
          onClose={handleCloseModal}
          trackId={newTrack.id}
          trackName={newTrack.trackName || 'New Track'}
          isSendingNotification={isSendingNotification}
        />
      )}
    </div>
  );
};

export default Dashboard;
